import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Box,
  CircularProgress,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createSupportTicket } from '../../services/supportTicketApi';
import TicketAttachmentField from '../support/TicketAttachmentField';

const TRAINING_ISSUE_TYPES = [
  { value: 'technical_problem', label: 'Technical Problem' },
  { value: 'something_doesnt_feel_right', label: 'Patient Behaviour Seems Wrong' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' }
];

const SETTING_LABELS = {
  outpatient_clinic: 'Outpatient Clinic',
  emergency_department: 'Emergency Department',
  inpatient_ward: 'Inpatient Ward',
  community_mental_health: 'Community MH',
  gp_surgery: 'GP Surgery',
  crisis_team: 'Crisis Team',
};

const TrainingReportIssueDialog = ({
  open,
  onClose,
  token,
  sessionId,
  scenarioBrief,
  totalTurns,
  assessmentsAdministered = [],
  voiceModeActive = false,
}) => {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);
  // "The patient said something odd" is far easier to show than to describe,
  // and this was the one report surface with nowhere to put the evidence
  // (ST-20260921-0002).
  const [attachment, setAttachment] = useState(null);

  const scenarioName = scenarioBrief?.visibilityLevel === 'blind'
    ? 'Blind scenario'
    : scenarioBrief?.name || 'Unknown';

  const scenarioSetting = SETTING_LABELS[scenarioBrief?.setting] || scenarioBrief?.setting || '';

  const handleSubmit = async () => {
    if (!issueType) {
      setError('Please select an issue type.');
      return;
    }
    if (!description.trim()) {
      setError('Please describe the issue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await createSupportTicket({
        issueType,
        description: description.trim(),
        // Omitted entirely when there is none, so the server sees no key rather
        // than an empty object it has to interpret.
        ...(attachment ? { attachment } : {}),
        context: {
          currentPage: 'training_session',
          journeyStage: 'training',
          trainingSessionId: sessionId || undefined,
          scenarioId: scenarioBrief?._id || scenarioBrief?.scenarioId || undefined,
          scenarioName: scenarioName,
          scenarioSetting: scenarioSetting,
          totalTurns: totalTurns || 0,
          assessmentsUsed: assessmentsAdministered,
          voiceModeActive,
        }
      }, token);

      setSuccessInfo(result.ticket);
    } catch (err) {
      const msg = err.response?.data?.msg || err.message || 'Failed to submit ticket.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIssueType('');
    setDescription('');
    setLoading(false);
    setError('');
    setSuccessInfo(null);
    // Without this a file chosen for one report stays attached to the next one,
    // which would quietly send the wrong screenshot from a later session.
    setAttachment(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Report Training Issue
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {successInfo ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your issue has been submitted successfully.
            </Alert>
            <Typography variant="h6" gutterBottom>
              Ticket: {successInfo.ticketNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The training team will investigate this issue. You can continue your session.
            </Typography>
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Session context summary */}
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Session Context (auto-attached)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip size="small" color="secondary" label="Training Platform" />
                {scenarioName && <Chip size="small" variant="outlined" label={scenarioName} />}
                {scenarioSetting && <Chip size="small" variant="outlined" label={scenarioSetting} />}
                <Chip size="small" variant="outlined" label={`Turn ${totalTurns || 0}`} />
                {voiceModeActive && <Chip size="small" color="info" label="Voice Mode" />}
                {assessmentsAdministered.length > 0 && (
                  <Chip size="small" variant="outlined" label={`${assessmentsAdministered.length} assessment(s)`} />
                )}
              </Box>
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="training-issue-type-label">What kind of issue?</InputLabel>
              <Select
                labelId="training-issue-type-label"
                value={issueType}
                label="What kind of issue?"
                onChange={(e) => setIssueType(e.target.value)}
              >
                {TRAINING_ISSUE_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Describe the issue"
              placeholder="What happened? What were you expecting to happen?"
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              helperText={`${description.length}/2000 characters`}
            />

            <TicketAttachmentField
              attachment={attachment}
              onChange={setAttachment}
              onError={setError}
              disabled={loading}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        {successInfo ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !issueType || !description.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? 'Submitting...' : 'Submit Issue'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrainingReportIssueDialog;
